
import React from 'react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Button } from "@/components/ui/button";
import { Clock } from "lucide-react";
import { TextHistoryItem } from './NoteState';

interface TextRecoveryDialogProps {
  showDialog: boolean;
  onOpenChange: (open: boolean) => void;
  textHistory: TextHistoryItem[];
  onRestore: (text: string) => void;
  currentContent: string;
}

const TextRecoveryDialog: React.FC<TextRecoveryDialogProps> = ({
  showDialog,
  onOpenChange,
  textHistory,
  onRestore,
  currentContent,
}) => {
  // Format date for display
  const formatDate = (date: Date): string => {
    return new Intl.DateTimeFormat('en-US', {
      month: 'short',
      day: 'numeric',
      hour: 'numeric',
      minute: 'numeric',
      hour12: true
    }).format(date);
  };

  return (
    <Dialog open={showDialog} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl w-[calc(100vw-2rem)] sm:w-full">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Clock className="h-4 w-4" />
            Text History
          </DialogTitle>
        </DialogHeader>
        <ScrollArea className="h-[60vh] sm:h-[300px] rounded-md border p-4">
          <div className="space-y-4">
            {/* Current version */}
            <div className="flex flex-col gap-2 p-2 bg-accent/50 rounded">
              <div className="flex items-center gap-2 flex-wrap">
                <span className="text-xs font-semibold bg-primary text-primary-foreground px-2 py-0.5 rounded">Current Version</span>
                <span className="text-xs text-muted-foreground">{formatDate(new Date())}</span>
              </div>
              <p className="text-sm text-muted-foreground line-clamp-2">{currentContent}</p>
            </div>

            {/* Past versions */}
            {textHistory.map((item, index) => (
              <div key={index} className="flex flex-col gap-2 p-2 hover:bg-accent/30 rounded border">
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="text-xs font-medium text-muted-foreground">Previous version</span>
                  <span className="text-xs text-muted-foreground">{formatDate(item.timestamp)}</span>
                </div>
                <p className="text-sm text-muted-foreground line-clamp-3">{item.content}</p>
                <Button
                  variant="outline"
                  size="sm"
                  className="self-end"
                  onClick={() => {
                    onRestore(item.content);
                    onOpenChange(false);
                  }}
                >
                  Restore
                </Button>
              </div>
            ))}
          </div>
        </ScrollArea>
      </DialogContent>
    </Dialog>
  );
};

export default TextRecoveryDialog;
